import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import {
  Calendar, ChevronLeft, ChevronRight, Clock, Plus,
  Video, MapPin, Users,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Badge,
  BetaBadge,
  Button,
  Card,
  IconButton,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import {
  EVENT_SOURCE_LABELS,
  EVENT_SOURCE_VARIANTS,
  type CalendarEventSource,
} from '@/styles/tokens.ts'

interface CalendarEvent {
  id: string
  title: string
  start: string
  end: string
  isAllDay: boolean
  location?: string
  attendees: string[]
  source: CalendarEventSource
  ticketId?: string
  color?: string
}

interface DayCell {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  events: CalendarEvent[]
}

type ViewMode = 'month' | 'week' | 'day'

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
]

function getMonthDays(year: number, month: number): DayCell[] {
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Monday-based: 0=Mon, 6=Sun
  let startOffset = firstDay.getDay() - 1
  if (startOffset < 0) startOffset = 6

  const days: DayCell[] = []

  // Previous month padding
  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i)
    days.push({ date: d, isCurrentMonth: false, isToday: false, events: [] })
  }

  // Current month
  for (let d = 1; d <= lastDay.getDate(); d++) {
    const date = new Date(year, month, d)
    date.setHours(0, 0, 0, 0)
    days.push({
      date,
      isCurrentMonth: true,
      isToday: date.getTime() === today.getTime(),
      events: [],
    })
  }

  // Next month padding (fill to 42 cells = 6 weeks)
  while (days.length < 42) {
    const d = new Date(year, month + 1, days.length - startOffset - lastDay.getDate() + 1)
    days.push({ date: d, isCurrentMonth: false, isToday: false, events: [] })
  }

  return days
}

function getWeekDays(date: Date): Date[] {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Monday
  const monday = new Date(d.setDate(diff))
  const days: Date[] = []
  for (let i = 0; i < 7; i++) {
    days.push(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i))
  }
  return days
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7) // 07:00 - 20:00

function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

function eventVariant(source: CalendarEvent['source']) {
  return EVENT_SOURCE_VARIANTS[source] ?? EVENT_SOURCE_VARIANTS.internal
}

export function KalenderPage() {
  const a = useAnonymize()
  const { isBeta } = useModuleStatus('kalender')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState<ViewMode>('month')

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Fenster grosszuegig um den Monat herum: 7 Tage davor (Padding-Tage aus dem
  // Vormonat in der Monatsansicht) bis zum Ende des ersten Tages im Folgemonat
  // (damit der letzte Monatstag vollstaendig — bis 23:59:59.999 — inkl. ist und
  // Wochenansichten am Monatsende keine Termine verlieren).
  const from = new Date(year, month, 1 - 7).toISOString()
  const to = new Date(year, month + 1, 7, 23, 59, 59, 999).toISOString()

  const { data: events = [] } = useQuery<CalendarEvent[]>({
    queryKey: ['calendar-events', from, to],
    queryFn: () => apiClient.get('/calendar/events', { params: { from, to } }).then((r) => r.data),
    retry: false,
  })

  const navigate = (direction: number) => {
    const d = new Date(currentDate)
    if (view === 'month') d.setMonth(d.getMonth() + direction)
    else if (view === 'week') d.setDate(d.getDate() + direction * 7)
    else d.setDate(d.getDate() + direction)
    setCurrentDate(d)
  }

  const goToday = () => setCurrentDate(new Date())

  // Distribute events into month cells
  const monthDays = getMonthDays(year, month)
  for (const event of events) {
    const eventDate = new Date(event.start)
    eventDate.setHours(0, 0, 0, 0)
    const cell = monthDays.find((c) => c.date.getTime() === eventDate.getTime())
    if (cell) cell.events.push(event)
  }

  const weekDays = getWeekDays(currentDate)

  const headingLabel =
    view === 'day'
      ? currentDate.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      : `${MONTHS[month]} ${year}`

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Calendar className="w-7 h-7 text-brand-500" />
            Kalender
            {isBeta ? <BetaBadge size="md" /> : null}
          </span>
        }
        description="Termine und Planung mit M365-Sync"
        actions={
          <Button variant="primary" leadingIcon={<Plus className="w-4 h-4" />}>
            Neuer Termin
          </Button>
        }
      />

      <Tabs value={view} onValueChange={(v) => setView(v as ViewMode)}>
        <Card>
          <Card.Body className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <IconButton
                icon={<ChevronLeft />}
                aria-label="Zurück"
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
              />
              <Button variant="ghost" size="sm" onClick={goToday}>
                Heute
              </Button>
              <IconButton
                icon={<ChevronRight />}
                aria-label="Weiter"
                variant="ghost"
                size="sm"
                onClick={() => navigate(1)}
              />
              <h2 className="text-h4 text-gray-900 ml-2">{headingLabel}</h2>
            </div>
            <TabsList className="border-b-0">
              <TabsTrigger value="month">Monat</TabsTrigger>
              <TabsTrigger value="week">Woche</TabsTrigger>
              <TabsTrigger value="day">Tag</TabsTrigger>
            </TabsList>
          </Card.Body>
        </Card>

        <div className="flex gap-4 text-caption text-gray-600">
          {(Object.keys(EVENT_SOURCE_LABELS) as CalendarEventSource[]).map((src) => (
            <span key={src} className="flex items-center gap-1.5">
              <Badge variant={eventVariant(src)} size="sm">
                {EVENT_SOURCE_LABELS[src]}
              </Badge>
            </span>
          ))}
        </div>

        <TabsContent value="month">
          <Card>
            <div className="grid grid-cols-7 border-b border-gray-200">
              {WEEKDAYS.map((day) => (
                <div key={day} className="px-2 py-2 text-caption font-semibold text-gray-500 text-center">
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {monthDays.map((cell, i) => (
                <div
                  key={i}
                  className={cn(
                    'min-h-[100px] border-b border-r border-gray-100 p-1',
                    !cell.isCurrentMonth && 'bg-gray-50',
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex items-center justify-center w-6 h-6 text-caption font-medium rounded-full',
                      cell.isToday
                        ? 'bg-brand-500 text-white'
                        : cell.isCurrentMonth
                          ? 'text-gray-900'
                          : 'text-gray-400',
                    )}
                  >
                    {cell.date.getDate()}
                  </span>
                  <div className="mt-0.5 space-y-0.5">
                    {cell.events.slice(0, 3).map((evt) => (
                      <Badge
                        key={evt.id}
                        variant={eventVariant(evt.source)}
                        size="sm"
                        className="w-full justify-start truncate"
                        title={evt.title}
                      >
                        {!evt.isAllDay && (
                          <span className="font-medium">{formatTime(evt.start)}</span>
                        )}
                        <span className="truncate">{a.freeText(evt.title)}</span>
                      </Badge>
                    ))}
                    {cell.events.length > 3 ? (
                      <div className="text-caption text-gray-500 px-1">
                        +{cell.events.length - 3} weitere
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="week">
          <Card>
            <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-gray-200">
              <div className="px-2 py-2" />
              {weekDays.map((d, i) => {
                const isToday = d.toDateString() === new Date().toDateString()
                return (
                  <div
                    key={i}
                    className={cn(
                      'px-2 py-2 text-center border-l border-gray-100',
                      isToday && 'bg-brand-50',
                    )}
                  >
                    <p className="text-caption text-gray-500">{WEEKDAYS[i]}</p>
                    <p
                      className={cn(
                        'text-body font-semibold',
                        isToday ? 'text-brand-600' : 'text-gray-900',
                      )}
                    >
                      {d.getDate()}
                    </p>
                  </div>
                )
              })}
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {HOURS.map((hour) => (
                <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-gray-50">
                  <div className="px-2 py-3 text-caption text-gray-400 text-right pr-3">{hour}:00</div>
                  {weekDays.map((d, di) => {
                    const dayEvents = events.filter((e) => {
                      const eDate = new Date(e.start)
                      return eDate.toDateString() === d.toDateString() && eDate.getHours() === hour
                    })
                    return (
                      <div key={di} className="border-l border-gray-100 px-1 py-1 min-h-[48px] space-y-0.5">
                        {dayEvents.map((evt) => (
                          <Badge
                            key={evt.id}
                            variant={eventVariant(evt.source)}
                            size="sm"
                            className="w-full justify-start truncate"
                            title={evt.title}
                          >
                            <span className="font-medium">{formatTime(evt.start)}</span>
                            <span className="truncate">{a.freeText(evt.title)}</span>
                          </Badge>
                        ))}
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="day">
          <Card>
            <div className="max-h-[700px] overflow-y-auto">
              {HOURS.map((hour) => {
                const hourEvents = events.filter((e) => {
                  const eDate = new Date(e.start)
                  return eDate.toDateString() === currentDate.toDateString() && eDate.getHours() === hour
                })
                return (
                  <div key={hour} className="flex border-b border-gray-50">
                    <div className="w-16 px-2 py-4 text-caption text-gray-400 text-right pr-3 flex-shrink-0">
                      {hour}:00
                    </div>
                    <div className="flex-1 px-2 py-2 border-l border-gray-100 min-h-[60px] space-y-1">
                      {hourEvents.map((evt) => (
                        <div
                          key={evt.id}
                          className="rounded-md border border-gray-200 bg-white shadow-card px-3 py-2 space-y-1"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-gray-700">
                              <Clock className="w-3.5 h-3.5" />
                              <span className="text-body font-medium">
                                {formatTime(evt.start)} – {formatTime(evt.end)}
                              </span>
                            </div>
                            <Badge variant={eventVariant(evt.source)} size="sm">
                              {EVENT_SOURCE_LABELS[evt.source]}
                            </Badge>
                          </div>
                          <p className="text-body font-semibold text-gray-900">
                            {a.freeText(evt.title)}
                          </p>
                          {evt.location ? (
                            <p className="text-caption flex items-center gap-1 text-gray-500">
                              <MapPin className="w-3 h-3" />
                              {a.freeText(evt.location)}
                            </p>
                          ) : null}
                          {evt.attendees.length > 0 ? (
                            <p className="text-caption flex items-center gap-1 text-gray-500">
                              <Users className="w-3 h-3" />
                              {evt.attendees.length} Teilnehmer
                            </p>
                          ) : null}
                          {evt.source === 'm365' ? (
                            <p className="text-caption flex items-center gap-1 text-gray-500">
                              <Video className="w-3 h-3" />
                              Teams-Meeting
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
